import React, { useMemo } from 'react';
import { QrCodeScanner as QrCodeScannerIcon } from '@mui/icons-material';
import { Drawer } from '@mui/material';
import Icon from '../../Assets/Icon/CustomIcon';
import { NavLink } from 'react-router-dom';
import logo_safeonImg from '../../Assets/Images/logo_safeon.png';
import Commons from '../../Services/Common/Common';
import MainDrawer from './MainDrawer';
import { useSelector, useDispatch } from 'react-redux';
import { DrawerActions } from '../../Reducer/DrawerReducer';
import useWeather from '../../Hooks/useWeather';
import PropTypes from 'prop-types';
import { useAppNavigate } from '../../Hooks/back/useAppNavigate';
import { TbmActions } from '../../Reducer/TbmReducer';

function BottomNavItem({ to, icon: IconComponent, label }) {
  return (
    <NavLink to={to} className="flex h-full flex-1 no-underline">
      {({ isActive }) => {
        const activeColor = isActive ? 'text-[#5ABD4F]' : 'text-[#94A3B8]';
        const activeFont = isActive ? 'font-bold' : 'font-medium';

        return (
          <div
            className={`flex w-full flex-col items-center justify-center gap-1 transition-all ${activeColor}`}
          >
            <div className="flex w-1/5 items-center justify-center">
              <IconComponent />
            </div>
            <span className={`text-[10px] leading-tight ${activeFont}`}>
              {label}
            </span>
          </div>
        );
      }}
    </NavLink>
  );
}

export default function AppBar({ handleBack }) {
  const { navigate } = useAppNavigate();
  const dispatch = useDispatch();
  const drawerOpen = useSelector((state) => state.drawers.open);
  const { userInfo } = useSelector((state) => state.BaseInfo);
  const menuInfo = useSelector((state) => state.BaseInfo.menuInfo);
  const { statusBar, navBar } = useSelector((state) => state.systemBar);
  const { type, bgColor, titleName } = useSelector((state) => state.navBarInfo);
  const { alarmFlag } = useSelector((state) => state.inAppNoti);
  const { participantData } = useSelector((state) => state.tbm);
  const { temp, WeatherIcon, iconColor } = useWeather();

  const toggleDrawer = (open) => () => {
    if (open) {
      dispatch(DrawerActions.OpenDrawer());
    } else {
      dispatch(DrawerActions.CloseDrawer());
    }
  };

  const menuMap = useMemo(
    () => Object.fromEntries(menuInfo.map((m) => [m.proId, m])),
    [menuInfo]
  );

  const bottomNavConfig = [
    { id: 'Home', icon: Icon.Home },
    { id: 'M_EMT_BASIC', icon: Icon.Phone },
    { id: 'Setting', icon: Icon.Person },
  ];

  if (type === 'titleBar') {
    return (
      <header
        className={`fixed top-0 flex w-full items-center px-4 ${
          titleName === '작업 내역'
            ? 'z-bottom-nav justify-between'
            : 'z-app-bar grid grid-cols-[1fr_auto_1fr]'
        }`}
        style={{
          paddingTop: `${statusBar}px`,
          height: `${statusBar + 56}px`,
          backgroundColor: bgColor || '#ffffff',
          color: titleName === '보이스 체크' ? '#F7F7F7' : '#000000',
        }}
      >
        {/* 왼쪽 영역: 뒤로가기 버튼 및 제목 */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleBack}
            className="-ml-2 flex h-10 w-10 items-center justify-center rounded-full transition-colors active:bg-black/5"
          >
            <Icon.ArrowBackIosNew
              className="h-6 w-6"
              style={{ color: 'inherit' }}
            />
          </button>
          {titleName === '작업 내역' && (
            <p className="text-[20px] leading-8 font-bold">{titleName}</p>
          )}
        </div>

        {/* 중앙 영역: 제목 (grid 모드일 때) */}
        {titleName !== '작업 내역' && (
          <div className="flex items-center justify-center">
            <p className="text-center text-[20px] leading-8 font-bold">
              {titleName}
            </p>
          </div>
        )}

        {/* 오른쪽 영역: 작업중지 버튼 또는 빈 공간 */}
        <div className="flex justify-end">
          {titleName === '작업 내역' && (
            <button
              className="flex items-center gap-1 rounded-lg bg-[#F55858] px-2.5 py-1.5 text-[14px] font-bold text-white active:opacity-80"
              onClick={() =>
                navigate('/M_WorkStop/write', { state: { gubun: 'list' } })
              }
            >
              <Icon.Emergency /> 작업 중지
            </button>
          )}
          {titleName === 'TBM' && participantData.hostYn === 'Y' && (
            <button
              type="button"
              className="flex h-10 w-10 items-center justify-center rounded-full text-black shadow-sm active:opacity-80"
              aria-label="TBM QR 열기"
              onClick={() => dispatch(TbmActions.setQrModal(true))}
            >
              <QrCodeScannerIcon sx={{ fontSize: 26 }} />
            </button>
          )}
        </div>
      </header>
    );
  }

  return (
    <>
      {/* 상단 Header */}
      <header
        className="z-app-bar fixed top-0 flex w-full items-center justify-between px-4"
        style={{
          paddingTop: `${statusBar}px`,
          height: `${statusBar + 56}px`,
          backgroundColor: bgColor,
        }}
      >
        <button
          className="ml-2 flex items-center justify-center p-2 active:opacity-70"
          onClick={() => {
            Commons.Storages('set', 'path', { nowPath: 1 });
            navigate('/Home');
          }}
        >
          <img
            src={logo_safeonImg}
            alt="SafeOn"
            className="h-6.25 w-34.5 object-contain"
          />
        </button>
        <div className="flex gap-1">
          <div className="flex items-center justify-center gap-2 rounded-full border border-slate-100 bg-white px-2.5">
            <div
              className="flex w-2/6 items-center justify-center"
              style={{ color: iconColor }}
            >
              <WeatherIcon />
            </div>
            <span className="text-xs leading-none font-bold text-slate-700">
              {temp}°C
            </span>
          </div>
          <button
            onClick={() => navigate('/M_AlarmList')}
            className="relative flex h-10 w-10 items-center justify-center rounded-full transition-colors active:bg-black/5"
          >
            <Icon.Notifications className="h-7 w-7 text-slate-600" />
            {alarmFlag && (
              <span className="absolute top-2 right-2 h-1 w-1 rounded-full bg-red-500" />
            )}
          </button>
          <button
            onClick={toggleDrawer(true)}
            className="flex h-10 w-10 items-center justify-center rounded-full transition-colors active:bg-black/5"
          >
            <Icon.GridView className="h-7 w-7 text-slate-600" />
          </button>
        </div>
      </header>

      {/* 사이드 메뉴 (Drawer) */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{
          className: 'w-full h-full rounded-none shadow-none',
        }}
      >
        <MainDrawer
          userInfo={userInfo}
          onClose={toggleDrawer(false)}
          statusBar={statusBar}
          navBar={navBar}
        />
      </Drawer>

      {/* 하단 Navigation */}
      <nav
        className="z-bottom-nav fixed bottom-0 flex h-16 w-full items-center justify-around border-t border-slate-100 bg-white"
        style={{ marginBottom: `${navBar}px` }}
      >
        {bottomNavConfig.map(({ id, icon, image }) => {
          const menu = menuMap[id];
          const path =
            id === 'Home'
              ? '/Home'
              : menu?.menuId
                ? `/${menu.menuId}`
                : `/${id}`;

          return (
            <BottomNavItem
              key={id}
              to={path}
              icon={icon}
              image={image}
              label={menu?.menuNm}
            />
          );
        })}
      </nav>
    </>
  );
}

AppBar.propTypes = {
  handleBack: PropTypes.func.isRequired,
};
